import React, { useState } from "react";
import { useDispatch } from "react-redux";

import { makeStyles } from "@material-ui/styles";
import MuiTabs from "@material-ui/core/Tabs";
import { Card, Divider, Tab } from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import ViewIcon from "@material-ui/icons/RemoveRedEye";
import DragHandleIcon from "@material-ui/icons/DragHandle";

// perfect scrollbar
import "react-perfect-scrollbar/dist/css/styles.css";
import PerfectScrollbar from "react-perfect-scrollbar";

import { Button, showNotification } from "jazasoft";

import { useDeepEffect, useDeepMemo, useMutation, useQuery } from "../../../hooks";
import { getDistinctValues, isEmpty, listFromCsv } from "../../../utils/helpers";
import { findSizeBreakupList, getCutOrderFlowInfoList } from "../../../helpers/orderHelper";
import Tabs from "../../../components/tab/Tabs";
import SubTab from "../../../components/tab/SubTab";
import { DEFAULT_SEPARATOR } from "../../../constants";
import Table from "../../../components/Table";
import FPOBreakupDrawer from "./FPOBreakupDrawer";

const Action = ({ idx, record }) => (
  <div style={{ width: 50 }}>
    <span className="handle">
      <DragHandleIcon style={{ cursor: "grab", marginLeft: 4 }} />
    </span>
  </div>
);

const useStyles = makeStyles({
  root: {
    padding: "1.5em"
  },
  mrTop: {
    marginTop: "1.5em"
  }
});

const FPOBreakup = props => {
  const { order, cutOrder, fpoOrderList, onRefresh } = props;
  const classes = useStyles();

  const [tabIdx, setTabIdx] = useState(0);
  const [styleColorTabIdx, setStyleColorTabIdx] = useState(0);
  const [editingSequence, setEditingSequence] = useState(false);
  const [fpoRows, setFpoRows] = useState([]);
  const [drawerState, setDrawerState] = useState({ open: false, action: null, fpoOrder: null });

  const parentCutOrderIds = getDistinctValues(fpoOrderList?.flatMap(e => listFromCsv(e.parentIds)) || []);

  const [mutate] = useMutation();
  const dispatch = useDispatch();

  const { data: layList = [] } = useQuery(
    !isEmpty(parentCutOrderIds) && { url: `lays`, params: { search: `orderFlow.id=in=(${parentCutOrderIds.join(",")})` } }
  );

  const fpoColumns = useDeepMemo(() => {
    let cols = [
      { dataKey: "dragAction", title: "Action" },
      { dataKey: "fpo", title: "External FPO" },
      { dataKey: "styles", title: "Style" },
      { dataKey: "colors", title: "Color" },
      { dataKey: "orderQty", title: "Order Qty", align: "right" },
      { dataKey: "fpoQty", title: "FPO Qty", align: "right" },
      { dataKey: "reqFpoQty", title: "Req. FPO Qty", align: "right" },
      { dataKey: "cutQty", title: "Cut Qty", align: "right" },
      { dataKey: "action", title: "Action", align: "center" }
    ];

    if (editingSequence) {
      cols = cols.filter(c => c.dataKey !== "action");
    } else {
      cols = cols.filter(c => c.dataKey !== "dragAction");
    }

    return cols;
  }, [editingSequence]);

  useDeepEffect(() => {
    if (isEmpty(fpoOrderList)) return;

    const flowInfoList = getCutOrderFlowInfoList(order, cutOrder);

    const rows = fpoOrderList
      .sort((a, b) => a.serialNo - b.serialNo)
      .map(fpoOrder => {
        const fiList = flowInfoList?.filter(fi => fi.externalFpo === fpoOrder.fpo) || [];
        const styleList = getDistinctValues(fiList.map(e => e.style).sort((a, b) => (a < b ? -1 : 1)));
        const colorList = getDistinctValues(fiList.map(e => e.color).sort((a, b) => (a < b ? -1 : 1)));
        const styles = styleList.join(",");
        const colors = colorList.join(",");
        const orderQty = fiList.reduce((acc, fi) => acc + fi.orderQty, 0);
        const fpoQty = fiList.reduce((acc, fi) => acc + (fi.fpoQty || 0), 0);

        const fpoSizeBreakupList = fpoOrder?.fpoSizeBreakupList || [];
        const reqFpoQty = fpoSizeBreakupList.reduce((acc, sb) => acc + sb.qty, 0);
        const cutSizeBreakupList = layList.flatMap(lay => {
          const breakupList = !isEmpty(lay.fpoBreakup) ? JSON.parse(lay.fpoBreakup) : [];
          const sizeBreakupList = breakupList.find(e => e.externalFpo === fpoOrder.fpo)?.sizeBreakupList || [];
          return sizeBreakupList;
        });
        const cutQty = cutSizeBreakupList.reduce((acc, sb) => acc + sb.qty, 0);
        const finalFpoOrder = { ...fpoOrder, styleList, colorList, styles, colors, orderQty, fpoQty, reqFpoQty, cutQty, flowInfoList: fiList };

        let actionElements = [
          <ViewIcon
            key="view"
            style={{ marginRight: 8, cursor: "pointer" }}
            onClick={() => setDrawerState({ open: true, action: "view", fpoOrder: finalFpoOrder })}
          />,
          <EditIcon
            key="edit"
            style={{ marginRight: 8, cursor: "pointer" }}
            onClick={() => setDrawerState({ open: true, action: "update", fpoOrder: finalFpoOrder })}
          />
        ];

        return {
          ...finalFpoOrder,
          dragAction: <Action record={fpoOrder} />,
          action: actionElements
        };
      });

    setFpoRows(rows);
  }, [order, cutOrder, fpoOrderList, layList]);

  const onSequenceUpdate = () => {
    const fpoOrderList = fpoRows.map(({ id, fpo }, idx) => ({ id, fpo, serialNo: idx + 1 }));
    const options = {
      url: `order-flow`,
      method: "put",
      params: { orderId: order?.id },
      data: fpoOrderList
    };
    mutate(options, {
      onSuccess: () => {
        dispatch(showNotification(`Bundle Order sequence updated successfully `));
        setEditingSequence(false);
        onRefresh && onRefresh();
      }
    });
  };

  const fpoStyleCount = fpoRows.map(e => e.styleList.length).sort((a, b) => b - a)[0];
  const fpoColorCount = fpoRows.map(e => e.colorList.length).sort((a, b) => b - a)[0];
  const styleColorList =
    fpoColorCount > 1 && fpoStyleCount > 1
      ? cutOrder?.styleColorList || []
      : fpoColorCount > 1
      ? cutOrder?.colorList || []
      : fpoStyleCount > 1
      ? cutOrder?.styleList || []
      : [];
  const styleColor = styleColorList[styleColorTabIdx];

  const { columns, headerRows, sizeKeys } = useDeepMemo(() => {
    if (isEmpty(order) || isEmpty(cutOrder)) return [];

    const sizeBreakupList = findSizeBreakupList(cutOrder, order);
    const sizeGroups = getDistinctValues(sizeBreakupList.map(sb => sb.sizeGroup));
    const sizeGroupIdx = sizeGroups.reduce((acc, e, idx) => ({ ...acc, [e]: idx % 2 }), {});
    const groupedCutPlan = getDistinctValues(sizeBreakupList.map(sb => sb.sizeGroup).filter(e => !isEmpty(e)))?.length > 1;

    let groupRow = {};
    let sizeGroup;
    const sizeList = sizeBreakupList.map((sb, idx) => {
      let leftBorder = false;
      if (sizeGroup !== sb.sizeGroup) {
        leftBorder = true;
        sizeGroup = sb.sizeGroup;
        groupRow[sb.sizeKey] = sb.sizeGroup;
      }
      return {
        dataKey: sb.sizeKey,
        title: sb.size,
        align: "center",
        sizeGroupIdx: sizeGroups.length > 1 ? sizeGroupIdx[sb.sizeGroup] : undefined,
        leftBorder,
        rightBorder: idx === sizeBreakupList.length - 1,
        colSpan: ({ column, row, rowIdx }) =>
          !groupedCutPlan || rowIdx === 1
            ? 1
            : isEmpty(row[column.dataKey])
            ? 0
            : getDistinctValues(sizeBreakupList.filter(sb => sb.sizeGroup === row[column.dataKey]).map(sb => sb.sizeKey))?.length
      };
    });

    let columns = [
      { dataKey: "fpo", title: "FPO", align: "left", rowSpan: ({ rowIdx }) => (rowIdx % 4 === 0 ? 4 : 0), leftBorder: true },
      { dataKey: "info", title: "Size", align: "left", leftBorder: true },
      ...sizeList,
      { dataKey: "total", title: "Total", leftBorder: true, rightBorder: true }
    ];
    if (tabIdx === 0) {
      columns = columns.filter(c => c.dataKey !== "fpo");
    }

    columns = columns.map(c => ({ ...c, align: c.align || "center" }));

    let headerRows = [];
    if (Object.keys(groupRow)?.length > 1) {
      headerRows = [groupRow, columns.reduce((acc, c) => ({ ...acc, [c.dataKey]: c.title }), {})];
    } else {
      headerRows = [columns.reduce((acc, c) => ({ ...acc, [c.dataKey]: c.title }), {})];
    }

    return { columns, headerRows, sizeKeys: sizeList.map(e => e.dataKey) };
  }, [order, cutOrder, tabIdx]);

  const rows = useDeepMemo(() => {
    if (isEmpty(order) || isEmpty(cutOrder)) return [];

    const finalFpoOrderList = tabIdx === 0 ? [{}] : fpoOrderList;

    const rows = finalFpoOrderList.flatMap(({ fpo }) => {
      let flowInfoList = getCutOrderFlowInfoList(order, cutOrder);
      flowInfoList = styleColor?.includes(DEFAULT_SEPARATOR)
        ? flowInfoList.filter(fi => fi.styleColor === styleColor)
        : fpoStyleCount > 1
        ? flowInfoList.filter(fi => fi.style === styleColor)
        : fpoColorCount > 1
        ? flowInfoList.filter(fi => fi.color === styleColor)
        : flowInfoList;
      const finalFlowInfoList = flowInfoList.filter(e => isEmpty(fpo) || fpo === e.externalFpo);

      const reqFpoSizeBreakupList = fpoOrderList.filter(e => isEmpty(fpo) || e.fpo === fpo).flatMap(fpo => fpo?.fpoSizeBreakupList || []);
      const finalReqFpoSizeBreakupList = styleColor?.includes(DEFAULT_SEPARATOR)
        ? reqFpoSizeBreakupList.filter(fi => fi.styleColor === styleColor)
        : fpoStyleCount > 1
        ? reqFpoSizeBreakupList.filter(fi => fi.style === styleColor)
        : fpoColorCount > 1
        ? reqFpoSizeBreakupList.filter(fi => fi.color === styleColor)
        : reqFpoSizeBreakupList;

      const cutSizeBreakupList = layList.flatMap(lay => {
        const breakupList = !isEmpty(lay.fpoBreakup) ? JSON.parse(lay.fpoBreakup) : [];
        return breakupList.filter(b => isEmpty(fpo) || fpo === b.externalFpo).flatMap(e => e.sizeBreakupList || []);
      });
      const finalCutBreakupList = styleColor?.includes(DEFAULT_SEPARATOR)
        ? cutSizeBreakupList.filter(fi => fi.styleColor === styleColor)
        : fpoStyleCount > 1
        ? cutSizeBreakupList.filter(fi => fi.style === styleColor)
        : fpoColorCount > 1
        ? cutSizeBreakupList.filter(fi => fi.color === styleColor)
        : cutSizeBreakupList;

      const sizeBreakupList = finalFlowInfoList.flatMap(fi => fi.sizeBreakupList || []);
      const fpoSizeBreakupList = finalFlowInfoList.flatMap(fi => fi.fpoSizeBreakupList || []);

      const totalOrderQty = sizeBreakupList.reduce((acc, sb) => acc + sb.qty, 0);
      const orderQty = sizeKeys.reduce(
        (acc, sizeKey) => ({ ...acc, [sizeKey]: sizeBreakupList.filter(sb => sb.sizeKey === sizeKey).reduce((acc, sb) => acc + sb.qty, 0) }),
        {}
      );
      const totalFpoQty = fpoSizeBreakupList.reduce((acc, sb) => acc + sb.qty, 0);
      const fpoQty = sizeKeys.reduce(
        (acc, sizeKey) => ({ ...acc, [sizeKey]: fpoSizeBreakupList.filter(sb => sb.sizeKey === sizeKey).reduce((acc, sb) => acc + sb.qty, 0) }),
        {}
      );

      const reqTotalFpoQty = finalReqFpoSizeBreakupList.reduce((acc, sb) => acc + sb.qty, 0);
      const reqFpoQty = sizeKeys.reduce(
        (acc, sizeKey) => ({
          ...acc,
          [sizeKey]: finalReqFpoSizeBreakupList.filter(sb => sb.sizeKey === sizeKey).reduce((acc, sb) => acc + sb.qty, 0)
        }),
        {}
      );

      const totalCutQty = finalCutBreakupList.reduce((acc, sb) => acc + sb.qty, 0);
      const cutQty = sizeKeys.reduce(
        (acc, sizeKey) => ({ ...acc, [sizeKey]: finalCutBreakupList.filter(sb => sb.sizeKey === sizeKey).reduce((acc, sb) => acc + sb.qty, 0) }),
        {}
      );

      const totalBalance = totalCutQty - reqTotalFpoQty;
      const balanceQty = sizeKeys.reduce((acc, sizeKey) => ({ ...acc, [sizeKey]: cutQty[sizeKey] - reqFpoQty[sizeKey] }), {});

      let rowList = [
        { fpo, info: "Order Qty", total: totalOrderQty, ...orderQty },
        { fpo, info: "FPO Qty", total: totalFpoQty, ...fpoQty },
        { fpo, info: "Req. FPO Qty", total: reqTotalFpoQty, ...reqFpoQty },
        { fpo, info: "Cut Qty", total: totalCutQty, ...cutQty },
        { fpo, info: "Balance", total: totalBalance, ...balanceQty }
      ];

      if (tabIdx === 1) {
        rowList = rowList.filter(e => e.info !== "Order Qty");
      }

      return rowList;
    });

    return rows;
  }, [order, cutOrder, tabIdx, styleColor, sizeKeys, fpoOrderList, layList]);

  return (
    <div className={classes.root}>
      <FPOBreakupDrawer
        open={drawerState.open || false}
        action={drawerState.action}
        fpoOrder={drawerState.fpoOrder}
        layList={layList}
        onDrawerClose={() => setDrawerState({ open: false })}
        onRefresh={onRefresh}
      />

      <Card>
        <PerfectScrollbar>
          <Table
            classes={{ table: classes.table }}
            columns={fpoColumns}
            rows={fpoRows}
            setRows={setFpoRows}
            // cellStyle={({ column }) => ({ width: column.dataKey === "state" ? 180 : undefined })}
          />
        </PerfectScrollbar>
        <div style={{ display: "flex", flexDirection: "row", justifyContent: "flex-end", padding: "0.5em 1.5em" }}>
          {!editingSequence && <Button label="Edit Sequence" variant="outlined" color="primary" onClick={() => setEditingSequence(true)} />}
          {editingSequence && <Button label="Cancel" variant="outlined" color="primary" onClick={() => setEditingSequence(false)} />}
          {editingSequence && <Button style={{ marginLeft: "1.5em" }} label="Save" variant="contained" color="primary" onClick={onSequenceUpdate} />}
        </div>
      </Card>

      <Card className={classes.mrTop}>
        <MuiTabs value={tabIdx} onChange={(e, tabIdx) => setTabIdx(tabIdx)}>
          <Tab label="FPO Breakup Summary" />
          <Tab label="FPO Breakup Detail" />
        </MuiTabs>
        <Divider />
        {styleColorList.length > 1 && (
          <Tabs style={{ margin: "1em" }} value={styleColorTabIdx} onChange={(e, tabIdx) => setStyleColorTabIdx(tabIdx)}>
            {styleColorList.map(styleColor => (
              <SubTab style={{ maxWidth: 320 }} key={styleColor} label={styleColor.split(DEFAULT_SEPARATOR).join(", ")} />
            ))}
          </Tabs>
        )}
        <div style={{ padding: "0 1em 1em 1em" }}>
          <Table
            classes={{ table: classes.table }}
            columns={columns}
            headerRows={headerRows}
            rows={rows}
            headerCellStyle={column => ({
              borderLeft: column.leftBorder ? "1px solid rgba(224, 224, 224, 1)" : undefined,
              borderRight: column.rightBorder ? "1px solid rgba(224, 224, 224, 1)" : undefined,
              backgroundColor: "#C6D0DA"
            })}
            cellStyle={({ column, record }) => ({
              borderLeft: column.leftBorder ? "1px solid rgba(224, 224, 224, 1)" : undefined,
              borderRight: column.rightBorder ? "1px solid rgba(224, 224, 224, 1)" : undefined
            })}
          />
        </div>
      </Card>
    </div>
  );
};

export default FPOBreakup;
